import React from 'react';

export default function App() {
  const projects = [
    {
      title: "TasteCircle",
      subtitle: "Food Sharing Application",
      period: "August 2025 - Present",
      description: "Daily food photo-sharing iOS app with AI-powered food classification and geospatial features. Generated 2,000+ pre-registrations through campus activation campaigns.",
      highlights: [
        { label: "Pre-registrations", value: "2,000+" }
      ],
      technologies: ["Flutter", "PostgreSQL", "Azure", "CLIP", "ArcGIS"],
      link: "https://tastecircle.org/",
      linkText: "Visit TasteCircle"
    },
    {
      title: "GIS Geospatial Analysis",
      subtitle: "Location Intelligence & Clustering",
      period: "2024 - Present",
      description: "Built automated location mapping and analysis systems using ArcGIS API for Python. Integrated CLIP-based image classification with geospatial clustering to analyze and categorize photo data, enhancing feed recommendation algorithms through location-aware insights.",
      highlights: [
        { label: "Tools", value: "ArcGIS Pro" }
      ],
      technologies: ["ArcPy", "PostGIS", "Python", "Clustering Algorithms"],
      link: "https://normanmapdata.netlify.app/",
      linkText: "View Live Demo"
    }
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.name}>Nathaniel Wolffe</h1>
          <p style={styles.title}>Backend Developer & AI Systems Architect</p>
          <div style={styles.contactRow}>
            <a href="mailto:natewolffe@gmail.com" style={styles.link}>
              natewolffe@gmail.com
            </a>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {projects.map((project, index) => (
          <article key={index} style={styles.project}>
            <div style={styles.projectHeader}>
              <h2 style={styles.projectTitle}>{project.title}</h2>
              <p style={styles.projectSubtitle}>{project.subtitle}</p>
              <p style={styles.projectPeriod}>{project.period}</p>
            </div>

            <p style={styles.description}>{project.description}</p>

            <div style={styles.highlightsRow}>
              {project.highlights.map((highlight, i) => (
                <div key={i} style={styles.highlight}>
                  <span style={styles.highlightLabel}>{highlight.label}</span>
                  <span style={styles.highlightValue}>{highlight.value}</span>
                </div>
              ))}
            </div>

            <div style={styles.techSection}>
              <span style={styles.techLabel}>Technologies</span>
              <div style={styles.techRow}>
                {project.technologies.map((tech, i) => (
                  <span key={i} style={styles.techTag}>{tech}</span>
                ))}
              </div>
            </div>

            <a href={project.link} style={styles.githubLink}>
              {project.linkText}
            </a>
          </article>
        ))}
      </main>

      <footer style={styles.footer}>
        <p>2025 Nathaniel Wolffe</p>
      </footer>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    color: '#111111',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    borderBottom: '1px solid #e5e5e5',
    backgroundColor: '#ffffff',
  },
  headerContent: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '80px 40px',
  },
  name: {
    fontSize: '48px',
    fontWeight: '600',
    margin: '0 0 12px 0',
    letterSpacing: '-1px',
    color: '#000000',
  },
  title: {
    fontSize: '20px',
    color: '#666666',
    margin: '0 0 24px 0',
    fontWeight: '400',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  link: {
    color: '#111111',
    textDecoration: 'none',
    borderBottom: '1px solid #111111',
    paddingBottom: '2px',
  },
  separator: {
    color: '#cccccc',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '60px 40px',
  },
  project: {
    marginBottom: '80px',
    paddingBottom: '80px',
    borderBottom: '1px solid #e5e5e5',
  },
  projectHeader: {
    marginBottom: '24px',
  },
  projectTitle: {
    fontSize: '28px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#000000',
  },
  projectSubtitle: {
    fontSize: '16px',
    color: '#666666',
    margin: '0 0 4px 0',
  },
  projectPeriod: {
    fontSize: '14px',
    color: '#999999',
    margin: '0',
  },
  description: {
    fontSize: '17px',
    lineHeight: '1.7',
    color: '#333333',
    margin: '0 0 32px 0',
  },
  highlightsRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '32px',
  },
  highlight: {
    backgroundColor: '#f5f5f5',
    padding: '16px 24px',
    borderLeft: '3px solid #000000',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  highlightLabel: {
    fontSize: '12px',
    color: '#666666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  highlightValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#000000',
  },
  techSection: {
    marginBottom: '24px',
  },
  techLabel: {
    fontSize: '12px',
    color: '#666666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'block',
    marginBottom: '12px',
  },
  techRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  techTag: {
    backgroundColor: '#f0f0f0',
    color: '#333333',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: '500',
  },
  githubLink: {
    color: '#111111',
    textDecoration: 'none',
    borderBottom: '1px solid #111111',
    paddingBottom: '2px',
    fontSize: '15px',
    fontWeight: '500',
  },
  footer: {
    borderTop: '1px solid #e5e5e5',
    backgroundColor: '#fafafa',
    padding: '32px 40px',
    textAlign: 'center',
    color: '#999999',
    fontSize: '14px',
  },
};
